import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { getErrorMessage } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Settings,
  Plus,
  Trash2,
  Pencil,
  ArrowUpFromLine,
  Ruler,
  Tags,
  Database,
  Download,
  History,
  CheckCircle,
  FileJson,
  FileSpreadsheet,
  Upload,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const SystemSettingsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("exit-reasons");
  
  // Exit Reasons State
  const [exitReasons, setExitReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(true);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [reasonForm, setReasonForm] = useState({ name: "", description: "" });

  // Entry Reasons State
  const [entryReasons, setEntryReasons] = useState([]);
  const [loadingEntryReasons, setLoadingEntryReasons] = useState(true);
  const [entryReasonDialogOpen, setEntryReasonDialogOpen] = useState(false);
  const [editingEntryReason, setEditingEntryReason] = useState(null);
  const [entryReasonForm, setEntryReasonForm] = useState({ name: "", description: "" });

  // Units State
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({ name: "", abbreviation: "" });

  // Categories State
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "" });

  // Backup State
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupHistory, setBackupHistory] = useState([]);
  const [loadingBackup, setLoadingBackup] = useState(true);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [downloadingJson, setDownloadingJson] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchExitReasons();
    fetchEntryReasons();
    fetchUnits();
    fetchCategories();
    fetchBackupData();
  }, []);

  // ==================== EXIT REASONS ====================
  const fetchExitReasons = async () => {
    try {
      const response = await api.get("/settings/exit-reasons");
      setExitReasons(response.data);
    } catch (error) {
      console.error("Error fetching exit reasons:", error);
    } finally {
      setLoadingReasons(false);
    }
  };

  const handleSaveReason = async (e) => {
    e.preventDefault();
    try {
      if (editingReason) {
        await api.put(`/settings/exit-reasons/${editingReason.id}`, reasonForm);
        toast.success("Motivo de saída atualizado!");
      } else {
        await api.post("/settings/exit-reasons", reasonForm);
        toast.success("Motivo de saída criado!");
      }
      setReasonDialogOpen(false);
      setEditingReason(null);
      setReasonForm({ name: "", description: "" });
      fetchExitReasons();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao salvar motivo"));
    }
  };

  const handleDeleteReason = async (reason) => {
    try {
      await api.delete(`/settings/exit-reasons/${reason.id}`);
      toast.success("Motivo de saída excluído!");
      fetchExitReasons();
    } catch (error) {
      toast.error("Erro ao excluir motivo");
    }
  };

  const openEditReason = (reason) => {
    setEditingReason(reason);
    setReasonForm({ name: reason.name, description: reason.description || "" });
    setReasonDialogOpen(true);
  };

  // ==================== ENTRY REASONS ====================
  const fetchEntryReasons = async () => {
    try {
      const response = await api.get("/settings/entry-reasons");
      setEntryReasons(response.data);
    } catch (error) {
      console.error("Error fetching entry reasons:", error);
    } finally {
      setLoadingEntryReasons(false);
    }
  };

  const handleSaveEntryReason = async (e) => {
    e.preventDefault();
    try {
      if (editingEntryReason) {
        await api.put(`/settings/entry-reasons/${editingEntryReason.id}`, entryReasonForm);
        toast.success("Motivo de entrada atualizado!");
      } else {
        await api.post("/settings/entry-reasons", entryReasonForm);
        toast.success("Motivo de entrada criado!");
      }
      setEntryReasonDialogOpen(false);
      setEditingEntryReason(null);
      setEntryReasonForm({ name: "", description: "" });
      fetchEntryReasons();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao salvar motivo"));
    }
  };

  const handleDeleteEntryReason = async (reason) => {
    try {
      await api.delete(`/settings/entry-reasons/${reason.id}`);
      toast.success("Motivo de entrada excluído!");
      fetchEntryReasons();
    } catch (error) {
      toast.error("Erro ao excluir motivo");
    }
  };

  const openEditEntryReason = (reason) => {
    setEditingEntryReason(reason);
    setEntryReasonForm({ name: reason.name, description: reason.description || "" });
    setEntryReasonDialogOpen(true);
  };

  // ==================== UNITS ====================
  const fetchUnits = async () => {
    try {
      const response = await api.get("/settings/units");
      setUnits(response.data);
    } catch (error) {
      console.error("Error fetching units:", error);
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleSaveUnit = async (e) => {
    e.preventDefault();
    try {
      if (editingUnit) {
        await api.put(`/settings/units/${editingUnit.id}`, unitForm);
        toast.success("Unidade atualizada com sucesso!");
      } else {
        await api.post("/settings/units", unitForm);
        toast.success("Unidade criada com sucesso!");
      }
      setUnitDialogOpen(false);
      setEditingUnit(null);
      setUnitForm({ name: "", abbreviation: "" });
      fetchUnits();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao salvar unidade"));
    }
  };

  const openEditUnit = (unit) => {
    setEditingUnit(unit);
    setUnitForm({ name: unit.name, abbreviation: unit.abbreviation || "" });
    setUnitDialogOpen(true);
  };

  const handleDeleteUnit = async (unit) => {
    try {
      await api.delete(`/settings/units/${unit.id}`);
      toast.success("Unidade excluída!");
      fetchUnits();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao excluir unidade"));
    }
  };

  // ==================== CATEGORIES ====================
  const fetchCategories = async () => {
    try {
      const response = await api.get("/settings/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/settings/categories/${editingCategory.id}`, categoryForm);
        toast.success("Categoria atualizada com sucesso!");
      } else {
        await api.post("/settings/categories", categoryForm);
        toast.success("Categoria criada com sucesso!");
      }
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      setCategoryForm({ name: "", description: "" });
      fetchCategories();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao salvar categoria"));
    }
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name, description: category.description || "" });
    setCategoryDialogOpen(true);
  };

  const handleDeleteCategory = async (category) => {
    try {
      await api.delete(`/settings/categories/${category.id}`);
      toast.success("Categoria excluída!");
      fetchCategories();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao excluir categoria"));
    }
  };

  // ==================== BACKUP ====================
  const fetchBackupData = async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        api.get("/backup/status"),
        api.get("/backup/history"),
      ]);
      setBackupStatus(statusRes.data);
      setBackupHistory(historyRes.data);
    } catch (error) {
      console.error("Error fetching backup data:", error);
    } finally {
      setLoadingBackup(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      await api.post("/backup/create");
      toast.success("Backup criado com sucesso!");
      fetchBackupData();
    } catch (error) {
      toast.error("Erro ao criar backup");
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleDownloadJson = async () => {
    setDownloadingJson(true);
    try {
      const response = await api.get("/backup/export/json", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const now = new Date().toISOString().slice(0, 19).replace(/[:-]/g, "");
      link.setAttribute("download", `backup_estoqueclinic_${now}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download JSON iniciado!");
    } catch (error) {
      toast.error("Erro ao baixar backup JSON");
    } finally {
      setDownloadingJson(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const response = await api.get("/backup/export/excel", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const now = new Date().toISOString().slice(0, 19).replace(/[:-]/g, "");
      link.setAttribute("download", `backup_estoqueclinic_${now}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download Excel iniciado!");
    } catch (error) {
      toast.error("Erro ao baixar backup Excel");
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleImportBackup = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".json")) {
      toast.error("Apenas arquivos JSON sao aceitos");
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/backup/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const imported = response.data.imported || {};
      const details = Object.entries(imported)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
      toast.success(`Dados importados! ${details}`);
      fetchBackupData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao importar dados"));
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
        <Settings className="h-12 w-12 mb-4 text-slate-300" />
        <p>Acesso restrito a administradores</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="system-settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
          Configurações do Sistema
        </h1>
        <p className="text-slate-500 mt-1">
          Gerencie motivos de saída, unidades, categorias e backups
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 md:grid-cols-5 h-auto gap-2">
          <TabsTrigger value="exit-reasons" className="gap-2" data-testid="tab-exit-reasons">
            <ArrowUpFromLine className="h-4 w-4" />
            Motivos de Saída
          </TabsTrigger>
          <TabsTrigger value="entry-reasons" className="gap-2" data-testid="tab-entry-reasons">
            <Download className="h-4 w-4" />
            Motivos de Entrada
          </TabsTrigger>
          <TabsTrigger value="units" className="gap-2" data-testid="tab-units">
            <Ruler className="h-4 w-4" />
            Unidades
          </TabsTrigger>
          <TabsTrigger value="categories" className="gap-2" data-testid="tab-categories">
            <Tags className="h-4 w-4" />
            Categorias
          </TabsTrigger>
          <TabsTrigger value="backup" className="gap-2" data-testid="tab-backup">
            <Database className="h-4 w-4" />
            Backup
          </TabsTrigger>
        </TabsList>

        {/* Exit Reasons Tab */}
        <TabsContent value="exit-reasons">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Motivos de Saída</CardTitle>
                  <CardDescription>
                    Configure os motivos disponíveis para saída de estoque
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setEditingReason(null);
                    setReasonForm({ name: "", description: "" });
                    setReasonDialogOpen(true);
                  }}
                  className="gap-2"
                  data-testid="add-exit-reason-btn"
                >
                  <Plus className="h-4 w-4" />
                  Novo Motivo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingReasons ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {exitReasons.map((reason) => (
                      <TableRow key={reason.id}>
                        <TableCell className="font-medium">{reason.name}</TableCell>
                        <TableCell className="text-slate-500">
                          {reason.description || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge className="alert-badge-success">Ativo</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditReason(reason)}
                              data-testid={`edit-reason-${reason.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="text-red-500"
                                  data-testid={`delete-reason-${reason.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir motivo?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteReason(reason)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Entry Reasons Tab */}
        <TabsContent value="entry-reasons">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Motivos de Entrada</CardTitle>
                  <CardDescription>
                    Configure os motivos disponíveis para entrada de estoque
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setEditingEntryReason(null);
                    setEntryReasonForm({ name: "", description: "" });
                    setEntryReasonDialogOpen(true);
                  }}
                  className="gap-2"
                  data-testid="add-entry-reason-btn"
                >
                  <Plus className="h-4 w-4" />
                  Adicionar Motivo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingEntryReasons ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : entryReasons.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Nenhum motivo de entrada cadastrado
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead className="w-24 text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entryReasons.map((reason) => (
                      <TableRow key={reason.id} className="table-row-zebra">
                        <TableCell className="font-medium">{reason.name}</TableCell>
                        <TableCell className="text-slate-500">
                          {reason.description || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditEntryReason(reason)}
                              data-testid={`edit-entry-reason-${reason.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="text-red-500"
                                  data-testid={`delete-entry-reason-${reason.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir motivo?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteEntryReason(reason)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Units Tab */}
        <TabsContent value="units">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Unidades de Medida</CardTitle>
                  <CardDescription>
                    Gerencie as unidades disponíveis para produtos
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setEditingUnit(null);
                    setUnitForm({ name: "", abbreviation: "" });
                    setUnitDialogOpen(true);
                  }}
                  className="gap-2"
                  data-testid="add-unit-btn"
                >
                  <Plus className="h-4 w-4" />
                  Nova Unidade
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingUnits ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Abreviação</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {units.map((unit) => (
                      <TableRow key={unit.id}>
                        <TableCell className="font-medium">{unit.name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{unit.abbreviation}</Badge>
                        </TableCell>
                        <TableCell>
                          {unit.is_default ? (
                            <Badge variant="outline">Padrão</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800">Customizada</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {!unit.is_default && (
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditUnit(unit)}
                                data-testid={`edit-unit-${unit.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="text-red-500"
                                    data-testid={`delete-unit-${unit.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Excluir unidade?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Esta ação não pode ser desfeita.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteUnit(unit)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Excluir
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Categorias de Produtos</CardTitle>
                  <CardDescription>
                    Gerencie as categorias disponíveis para produtos
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setEditingCategory(null);
                    setCategoryForm({ name: "", description: "" });
                    setCategoryDialogOpen(true);
                  }}
                  className="gap-2"
                  data-testid="add-category-btn"
                >
                  <Plus className="h-4 w-4" />
                  Nova Categoria
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingCategories ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Produtos</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell className="font-medium">{category.name}</TableCell>
                        <TableCell className="text-slate-500">
                          {category.description || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{category.product_count}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditCategory(category)}
                              data-testid={`edit-category-${category.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {!category.id.startsWith("product_") && category.product_count === 0 && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="text-red-500"
                                    data-testid={`delete-category-${category.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Excluir categoria?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Esta ação não pode ser desfeita.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteCategory(category)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Excluir
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Actions */}
            <div className="space-y-6">
              {/* Manual Backup Card */}
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="font-heading">Backup Manual</CardTitle>
                  <CardDescription>
                    Crie um backup completo do sistema a qualquer momento
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                      <span className="font-medium">Backup Automatico</span>
                      <Badge className="alert-badge-success">Ativo</Badge>
                    </div>
                    <p className="text-sm text-slate-500">
                      Backups sao realizados automaticamente a cada 24 horas
                    </p>
                  </div>
                  
                  {backupStatus?.last_backup && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <span className="font-medium">Ultimo backup:</span>{" "}
                        {formatDateTime(backupStatus.last_backup.created_at)}
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={handleCreateBackup}
                    disabled={creatingBackup}
                    className="w-full gap-2"
                    data-testid="create-backup-btn"
                  >
                    {creatingBackup ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Database className="h-4 w-4" />
                        Criar Backup Manual
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Download Card */}
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Download className="h-5 w-5 text-slate-500" />
                    Baixar Backup
                  </CardTitle>
                  <CardDescription>
                    Baixe uma copia dos seus dados no computador
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    onClick={handleDownloadJson}
                    disabled={downloadingJson}
                    variant="outline"
                    className="w-full gap-2 justify-start"
                    data-testid="download-json-btn"
                  >
                    {downloadingJson ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileJson className="h-4 w-4 text-blue-600" />
                    )}
                    Baixar JSON (backup completo)
                  </Button>
                  <Button
                    onClick={handleDownloadExcel}
                    disabled={downloadingExcel}
                    variant="outline"
                    className="w-full gap-2 justify-start"
                    data-testid="download-excel-btn"
                  >
                    {downloadingExcel ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    )}
                    Baixar Excel (visualizacao)
                  </Button>
                </CardContent>
              </Card>

              {/* Import Card */}
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Upload className="h-5 w-5 text-slate-500" />
                    Importar Dados
                  </CardTitle>
                  <CardDescription>
                    Restaure dados a partir de um arquivo JSON de backup
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <label
                    htmlFor="import-file"
                    className={`flex items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      importing
                        ? "border-slate-300 bg-slate-50"
                        : "border-slate-300 hover:border-primary hover:bg-primary/5"
                    }`}
                  >
                    {importing ? (
                      <div className="flex items-center gap-2 text-slate-500">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Importando dados...
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="h-6 w-6 mx-auto text-slate-400 mb-1" />
                        <p className="text-sm text-slate-500">
                          Clique para selecionar arquivo JSON
                        </p>
                      </div>
                    )}
                    <input
                      id="import-file"
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={handleImportBackup}
                      disabled={importing}
                      data-testid="import-file-input"
                    />
                  </label>
                  <p className="text-xs text-slate-400 mt-2">
                    Dados existentes serao atualizados. Novos dados serao adicionados.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - History */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <History className="h-5 w-5 text-slate-500" />
                  Historico de Backups
                </CardTitle>
                <CardDescription>
                  Ultimos backups realizados
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingBackup ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : backupHistory.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">
                    Nenhum backup encontrado
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {backupHistory.map((backup) => (
                      <div
                        key={backup.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-sm">
                            {backup.type === "manual" ? "Backup Manual" : "Backup Automatico"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatDateTime(backup.created_at)} {backup.created_by && `\u2022 ${backup.created_by}`}
                          </p>
                          {backup.data && (
                            <p className="text-xs text-slate-400 mt-1">
                              {backup.data.products_count || 0} prod. | {backup.data.stock_entries_count || 0} ent. | {backup.data.stock_exits_count || 0} said.
                            </p>
                          )}
                        </div>
                        <Badge className="alert-badge-success">{backup.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Exit Reason Dialog */}
      <Dialog open={reasonDialogOpen} onOpenChange={setReasonDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingReason ? "Editar Motivo de Saída" : "Novo Motivo de Saída"}
            </DialogTitle>
            <DialogDescription>
              Configure um motivo para saída de estoque
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveReason}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reason-name">Nome *</Label>
                <Input
                  id="reason-name"
                  value={reasonForm.name}
                  onChange={(e) => setReasonForm({ ...reasonForm, name: e.target.value })}
                  required
                  data-testid="reason-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reason-description">Descrição</Label>
                <Input
                  id="reason-description"
                  value={reasonForm.description}
                  onChange={(e) => setReasonForm({ ...reasonForm, description: e.target.value })}
                  data-testid="reason-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReasonDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-reason-btn">
                {editingReason ? "Salvar" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Entry Reason Dialog */}
      <Dialog open={entryReasonDialogOpen} onOpenChange={setEntryReasonDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingEntryReason ? "Editar Motivo de Entrada" : "Novo Motivo de Entrada"}
            </DialogTitle>
            <DialogDescription>
              Configure um motivo para entrada de estoque
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEntryReason}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="entry-reason-name">Nome *</Label>
                <Input
                  id="entry-reason-name"
                  value={entryReasonForm.name}
                  onChange={(e) => setEntryReasonForm({ ...entryReasonForm, name: e.target.value })}
                  required
                  data-testid="entry-reason-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="entry-reason-description">Descrição</Label>
                <Input
                  id="entry-reason-description"
                  value={entryReasonForm.description}
                  onChange={(e) => setEntryReasonForm({ ...entryReasonForm, description: e.target.value })}
                  data-testid="entry-reason-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEntryReasonDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-entry-reason-btn">
                {editingEntryReason ? "Salvar" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Unit Dialog */}
      <Dialog open={unitDialogOpen} onOpenChange={setUnitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingUnit ? "Editar Unidade de Medida" : "Nova Unidade de Medida"}
            </DialogTitle>
            <DialogDescription>
              {editingUnit ? "Atualize os dados da unidade de medida" : "Adicione uma nova unidade de medida customizada"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveUnit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="unit-name">Nome *</Label>
                <Input
                  id="unit-name"
                  value={unitForm.name}
                  onChange={(e) => setUnitForm({ ...unitForm, name: e.target.value })}
                  placeholder="Ex: Sachê"
                  required
                  data-testid="unit-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit-abbreviation">Abreviação</Label>
                <Input
                  id="unit-abbreviation"
                  value={unitForm.abbreviation}
                  onChange={(e) => setUnitForm({ ...unitForm, abbreviation: e.target.value })}
                  placeholder="Ex: SCH"
                  maxLength={6}
                  data-testid="unit-abbreviation-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUnitDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-unit-btn">
                {editingUnit ? "Salvar" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCategory ? "Editar Categoria" : "Nova Categoria"}
            </DialogTitle>
            <DialogDescription>
              {editingCategory ? "Atualize os dados da categoria" : "Adicione uma nova categoria de produtos"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveCategory}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="category-name">Nome *</Label>
                <Input
                  id="category-name"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="Ex: Toxinas"
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category-description">Descrição</Label>
                <Input
                  id="category-description"
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  data-testid="category-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-category-btn">
                {editingCategory ? "Salvar" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemSettingsPage;
